import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useSnackbar } from 'notistack';
import { X, Loader2, Target, Trophy, Users, Bot } from 'lucide-react';
import { api } from '../../../services/api';
import { GrupoTipoAcao, GrupoCargo } from '../../../types/grupos';

interface TipoAcaoModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  grupoId: string;
  tipoAcaoToEdit?: GrupoTipoAcao | null;
}

export function TipoAcaoModal({
  open,
  onClose,
  onSuccess,
  grupoId,
  tipoAcaoToEdit
}: TipoAcaoModalProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [cargos, setCargos] = useState<GrupoCargo[]>([]);
  const [formData, setFormData] = useState({
    descricao: '',
    pontuacao_meta: 0,
    brcoins_valor: 0,
    id_servidor_discord: '',
    id_canal_discord: '',
  });
  const [selectedCargosIds, setSelectedCargosIds] = useState<string[]>([]);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Carregar cargos do grupo quando abrir o modal
  useEffect(() => {
    if (open && grupoId) {
      api.get(`/grupos-cargos/grupo/${grupoId}`)
        .then(res => setCargos(Array.isArray(res.data) ? res.data : []))
        .catch(() => setCargos([]));
    }
  }, [open, grupoId]);

  useEffect(() => {
    if (tipoAcaoToEdit) {
      setFormData({
        descricao: tipoAcaoToEdit.descricao,
        pontuacao_meta: tipoAcaoToEdit.pontuacao_meta,
        brcoins_valor: tipoAcaoToEdit.brcoins_valor ?? 0,
        id_servidor_discord: tipoAcaoToEdit.id_servidor_discord ?? '',
        id_canal_discord: tipoAcaoToEdit.id_canal_discord ?? '',
      });
      setSelectedCargosIds(tipoAcaoToEdit.cargos?.map(c => c.id_cargo) ?? []);
    } else {
      setFormData({
        descricao: '',
        pontuacao_meta: 0,
        brcoins_valor: 0,
        id_servidor_discord: '',
        id_canal_discord: '',
      });
      setSelectedCargosIds([]);
    }
    setErrors({});
  }, [tipoAcaoToEdit, open]);

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.descricao.trim()) {
      newErrors.descricao = 'Descrição é obrigatória';
    } else if (formData.descricao.length < 3) {
      newErrors.descricao = 'Descrição deve ter pelo menos 3 caracteres';
    } else if (formData.descricao.length > 100) {
      newErrors.descricao = 'Descrição deve ter no máximo 100 caracteres';
    }

    if (formData.pontuacao_meta < 0) {
      newErrors.pontuacao_meta = 'Pontuação meta deve ser maior ou igual a 0';
    } else if (formData.pontuacao_meta > 1000) {
      newErrors.pontuacao_meta = 'Pontuação meta deve ser menor ou igual a 1000';
    }

    if (formData.brcoins_valor < 0) {
      newErrors.brcoins_valor = 'Valor em BRCoins deve ser maior ou igual a 0';
    } else if (formData.brcoins_valor > 10000) {
      newErrors.brcoins_valor = 'Valor em BRCoins deve ser menor ou igual a 10000';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      if (tipoAcaoToEdit?.id) {
        // Editar tipo de ação existente
        await api.put(`/grupos-tipos-acoes/${tipoAcaoToEdit.id}`, {
          descricao: formData.descricao,
          pontuacao_meta: formData.pontuacao_meta,
          brcoins_valor: formData.brcoins_valor,
          cargos_ids: selectedCargosIds,
          id_servidor_discord: formData.id_servidor_discord || null,
          id_canal_discord: formData.id_canal_discord || null,
        });
        enqueueSnackbar('Tipo de ação atualizado com sucesso!', { variant: 'success' });
      } else {
        // Criar novo tipo de ação
        await api.post('/grupos-tipos-acoes', {
          id_grupo: grupoId,
          descricao: formData.descricao,
          pontuacao_meta: formData.pontuacao_meta,
          brcoins_valor: formData.brcoins_valor,
          cargos_ids: selectedCargosIds,
          id_servidor_discord: formData.id_servidor_discord || null,
          id_canal_discord: formData.id_canal_discord || null,
        });
        enqueueSnackbar('Tipo de ação criado com sucesso!', { variant: 'success' });
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Erro ao salvar tipo de ação:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Erro ao salvar tipo de ação',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const toggleCargo = (cargoId: string) => {
    setSelectedCargosIds(prev =>
      prev.includes(cargoId) ? prev.filter(id => id !== cargoId) : [...prev, cargoId]
    );
  };

  const handleInputChange = (field: string) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = field === 'pontuacao_meta' || field === 'brcoins_valor'
      ? parseInt(event.target.value) || 0
      : event.target.value;

    setFormData(prev => ({ ...prev, [field]: value }));

    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
        <Dialog.Content className="fixed left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-background border border-border rounded-lg shadow-lg p-6 w-full max-w-md z-50 max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <Dialog.Title className="text-xl font-semibold text-foreground flex items-center space-x-2">
              <Target size={20} className="text-blue-600" />
              <span>{tipoAcaoToEdit ? 'Editar Tipo de Ação' : 'Nova Ação'}</span>
            </Dialog.Title>
            <Dialog.Close
              className="text-muted-foreground hover:text-foreground transition-colors"
              disabled={loading}
            >
              <X size={20} />
            </Dialog.Close>
          </div>

          {/* Form */}
          <div className="space-y-4">
            {/* Descrição */}
            <div>
              <label htmlFor="descricao" className="block text-sm font-medium text-foreground mb-1">
                Descrição da Ação *
              </label>
              <input
                id="descricao"
                type="text"
                value={formData.descricao}
                onChange={handleInputChange('descricao')}
                placeholder="Ex: Presença em Treinamento, Missão Concluída"
                disabled={loading}
                autoFocus
                className={`w-full px-3 py-2 border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.descricao
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-border'
                }`}
              />
              {errors.descricao && (
                <p className="mt-1 text-sm text-red-600">{errors.descricao}</p>
              )}
              {!errors.descricao && (
                <p className="mt-1 text-sm text-muted-foreground">
                  Descreva qual ação será registrada pelos membros
                </p>
              )}
            </div>

            {/* Pontuação Meta */}
            <div>
              <label htmlFor="pontuacao_meta" className="block text-sm font-medium text-foreground mb-1">
                Pontuação Meta
              </label>
              <input
                id="pontuacao_meta"
                type="number"
                min="0"
                max="1000"
                value={formData.pontuacao_meta}
                onChange={handleInputChange('pontuacao_meta')}
                placeholder="Ex: 10, 50, 100"
                disabled={loading}
                className={`w-full px-3 py-2 border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.pontuacao_meta
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-border'
                }`}
              />
              {errors.pontuacao_meta && (
                <p className="mt-1 text-sm text-red-600">{errors.pontuacao_meta}</p>
              )}
              {!errors.pontuacao_meta && (
                <p className="mt-1 text-sm text-muted-foreground">
                  Quantos pontos esta ação vale (0 - 1000)
                </p>
              )}
            </div>

            {/* BRCoins */}
            <div>
              <label htmlFor="brcoins_valor" className="block text-sm font-medium text-foreground mb-1">
                BRCoins ao Aprovar
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-yellow-500 text-sm font-bold select-none">₿</span>
                <input
                  id="brcoins_valor"
                  type="number"
                  min="0"
                  max="10000"
                  value={formData.brcoins_valor}
                  onChange={handleInputChange('brcoins_valor')}
                  placeholder="Ex: 5, 10, 50"
                  disabled={loading}
                  className={`w-full pl-8 pr-3 py-2 border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-yellow-500 ${
                    errors.brcoins_valor
                      ? 'border-red-500 focus:ring-red-500'
                      : 'border-border'
                  }`}
                />
              </div>
              {errors.brcoins_valor && (
                <p className="mt-1 text-sm text-red-600">{errors.brcoins_valor}</p>
              )}
              {!errors.brcoins_valor && (
                <p className="mt-1 text-sm text-muted-foreground">
                  Quantas BRCoins o militar ganha ao ter esta ação aprovada (0 = nenhuma)
                </p>
              )}
            </div>

            {/* Discord */}
            <div className="pt-2 border-t border-border">
              <label className="flex items-center gap-1 text-sm font-medium text-foreground mb-3">
                <Bot size={14} className="text-indigo-500" />
                Integração Discord <span className="text-muted-foreground font-normal">(opcional)</span>
              </label>
              <div className="space-y-3">
                <div>
                  <label htmlFor="id_servidor_discord" className="block text-xs text-muted-foreground mb-1">
                    ID do Servidor
                  </label>
                  <input
                    id="id_servidor_discord"
                    type="text"
                    value={formData.id_servidor_discord}
                    onChange={handleInputChange('id_servidor_discord')}
                    placeholder="Ex: 123456789012345678"
                    disabled={loading}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label htmlFor="id_canal_discord" className="block text-xs text-muted-foreground mb-1">
                    ID do Canal
                  </label>
                  <input
                    id="id_canal_discord"
                    type="text"
                    value={formData.id_canal_discord}
                    onChange={handleInputChange('id_canal_discord')}
                    placeholder="Ex: 987654321098765432"
                    disabled={loading}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
            </div>

            {/* Cargos com Acesso */}
            {cargos.length > 0 && (              <div>
                <label className="flex items-center gap-1 text-sm font-medium text-foreground mb-1">
                  <Users size={14} />
                  Cargos com Acesso
                </label>
                <div className="border border-border rounded-md p-3 space-y-2 max-h-40 overflow-y-auto">
                  {cargos.map(cargo => (
                    <label key={cargo.id} className="flex items-center gap-2 cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5">
                      <input
                        type="checkbox"
                        checked={selectedCargosIds.includes(cargo.id)}
                        onChange={() => toggleCargo(cargo.id)}
                        disabled={loading}
                        className="rounded border-border text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-foreground">{cargo.cargo}</span>
                    </label>
                  ))}
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  {selectedCargosIds.length === 0
                    ? 'Nenhum selecionado — todos os membros verão esta ação'
                    : `${selectedCargosIds.length} cargo(s) selecionado(s) — apenas esses verão esta ação`}
                </p>
              </div>
            )}

            {/* Preview */}
            {formData.descricao && (
              <div className="mt-4 p-3 border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/30 rounded-md">
                <div className="flex items-center space-x-2 text-blue-800 dark:text-blue-300">
                  <Trophy size={16} />
                  <span className="text-sm font-medium">Preview:</span>
                </div>
                <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
                  <strong>{formData.descricao}</strong> — {formData.pontuacao_meta} pontos
                  {formData.brcoins_valor > 0 && (
                    <span className="ml-2 text-yellow-600 dark:text-yellow-400 font-medium">
                      · {formData.brcoins_valor} BRCoins
                    </span>
                  )}
                </p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-foreground bg-background border border-border rounded-md hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 flex items-center space-x-2"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              <span>{loading ? 'Salvando...' : tipoAcaoToEdit ? 'Atualizar' : 'Criar'}</span>
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

